import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
// Импортируем тип для продукта, его можно расширить, включая поля для вариантов, изображений и пр.
import { Product } from "../../services/types/product.type";

export const productsApi = createApi({
  reducerPath: "productsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_ADMIN_API_URL, // например, "https://api.miyukon.store/admin"
    credentials: "include",
  }),
  endpoints: (builder) => ({
    // Получение списка всех вещей
    getProducts: builder.query<Product[], void>({
      query: () => "/products",
      transformResponse: (response: {
        status: string;
        message: string;
        data: Product[];
      }) => response.data,
    }),
    // Получение детальной информации о конкретной вещи
    // Параметр fullImages (если true) позволяет вернуть все изображения, а не только с imageType = "cover"
    getProductById: builder.query<
      Product,
      { uuid: string; fullImages?: boolean }
    >({
      query: ({ uuid, fullImages }) => {
        let url = `/products/${uuid}`;
        if (fullImages) {
          url += "?fullImages=true";
        }
        return { url, method: "GET" };
      },
      transformResponse: (response: {
        status: string;
        message: string;
        data: Product;
      }) => response.data,
    }),
    // Создание новой вещи
    // Ожидается, что данные отправляются в формате FormData, в котором передаются обычные поля (name, description, price, productTypeUuid, collectionUuid и т.д.)
    // А также массив фотографий с их метаданными (imageType, altText, position)
    createProduct: builder.mutation<Product, FormData>({
      query: (formData) => ({
        url: "/products",
        method: "POST",
        body: formData,
      }),
      transformResponse: (response: {
        status: string;
        message: string;
        data: Product;
      }) => response.data,
    }),
    // Обновление вещи
    // Здесь также используется FormData, позволяющая обновлять как текстовые данные, так и изображения.
    // При редактировании можно обновлять основные поля вещи, а также фотографий — обновлять метаданные, менять порядок, добавлять новые файлы.
    updateProduct: builder.mutation<
      Product,
      { uuid: string; body: Record<string, any> }
    >({
      query: ({ uuid, body }) => ({
        url: `/products/${uuid}`,
        method: "PATCH",
        body, // Передаётся JSON-объект с данными товара
      }),
      transformResponse: (response: {
        status: string;
        message: string;
        data: Product;
      }) => response.data,
    }),
    // Удаление вещи (soft delete)
    deleteProduct: builder.mutation<{ uuid: string }, string>({
      query: (uuid) => ({
        url: `/products/${uuid}`,
        method: "DELETE",
      }),
      // В ответе возвращаем лишь идентификатор удалённой вещи
      transformResponse: (_: any, __: any, arg: string) => ({ uuid: arg }),
    }),
    // Если потребуется, можно добавить эндпоинты для работы с отдельными изображениями
    // Например: загрузка нового изображения для уже существующей вещи, обновление конкретной фотографии и т.д.
  }),
});

export const {
  useGetProductsQuery,
  useGetProductByIdQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
} = productsApi;
